import type { AttributeSelectorProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/attribute-selector/types'
import type { MultibuySelectProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/multibuy/multibuy-select/types'
import { StockStatuses, SelectStates } from '@dcxwam/dcx-wam-ui-foundations'
import { MultiBuyPriceEntry } from '../../../services/product/product-data'
import { formatPrice, FormatPriceOptions } from '../../../utils/format-price'
import { MultibuyUomCategory, ShopConfig } from './config'

interface UnitLabelParams {
  categoryIdentifier?: string
  unitOfMeasure?: string
  multibuyUomCategories?: MultibuyUomCategory[]
}

interface MultibuyPropsParams {
  multiBuyPrice?: MultiBuyPriceEntry[]
  shopConfig: ShopConfig
  categoryIdentifier?: string
  unitOfMeasure?: string
  priceCurrency?: string
  pageLocale?: string
  selectedQty: number
  onChange: (qty: number) => void
  isDisabled?: boolean
  stockLevel?: number
  formatPriceOptions?: FormatPriceOptions
}

interface MultibuyItemBase {
  value: string
  attributeLabel: string
  price: string | undefined
  unitPrice: string | undefined
  badge: string | undefined
  disabled?: boolean
}

export const getMultibuyUnitLabel = (params: UnitLabelParams) => {
  const { categoryIdentifier, unitOfMeasure, multibuyUomCategories } = params

  if (!multibuyUomCategories?.length || !categoryIdentifier) {
    return { singular: '', plural: '' }
  }

  const category = multibuyUomCategories.find(
    (cat) => cat.categoryIdentifier === categoryIdentifier
  )

  if (!category?.unitsOfMeasure?.length) {
    return { singular: '', plural: '' }
  }

  const uomEntry = category.unitsOfMeasure.find(
    (uom) => uom.uomCode === unitOfMeasure
  )

  if (!uomEntry) {
    return { singular: '', plural: '' }
  }

  return {
    singular: uomEntry.unitLabelSingular ?? '',
    plural: uomEntry.unitLabelPlural ?? ''
  }
}

export const getPricePerUnitLabel = (
  params: UnitLabelParams
): string | undefined => {
  const { categoryIdentifier, unitOfMeasure, multibuyUomCategories } = params

  if (!multibuyUomCategories?.length || !categoryIdentifier || !unitOfMeasure) {
    return undefined
  }

  return multibuyUomCategories
    .find((cat) => cat.categoryIdentifier === categoryIdentifier)
    ?.unitsOfMeasure?.find((uom) => uom.uomCode === unitOfMeasure)
    ?.pricePerUnitLabel
}

const getDefaultSelectedQty = (params: {
  multiBuyPrice?: MultiBuyPriceEntry[]
  bestValueToggle?: boolean
  stockLevel?: number
}): number => {
  const { multiBuyPrice, bestValueToggle, stockLevel } = params

  if (!multiBuyPrice?.length) {
    return 1
  }

  const isTierInStock = (entry: MultiBuyPriceEntry): boolean =>
    stockLevel === undefined || entry.qty <= stockLevel

  if (bestValueToggle) {
    const bestValueEntry = multiBuyPrice.find((entry) => entry.bestValue)
    if (bestValueEntry && isTierInStock(bestValueEntry)) {
      return bestValueEntry.qty
    }
  }

  const singleEntry = multiBuyPrice.find((entry) => entry.qty === 1)
  if (singleEntry && isTierInStock(singleEntry)) {
    return singleEntry.qty
  }

  const firstInStock = multiBuyPrice.find(isTierInStock)
  return firstInStock?.qty ?? 1
}

export const getMultibuyDefaultQty = (params: {
  multiBuyPrice?: MultiBuyPriceEntry[]
  bestValueToggle?: boolean
  stockLevel?: number
}): number => getDefaultSelectedQty(params)

const buildMultibuyItems = (params: {
  multiBuyPrice?: MultiBuyPriceEntry[]
  shopConfig: ShopConfig
  categoryIdentifier?: string
  unitOfMeasure?: string
  priceCurrency?: string
  pageLocale?: string
  stockLevel?: number
  formatPriceOptions?: FormatPriceOptions
}): MultibuyItemBase[] => {
  const {
    multiBuyPrice,
    shopConfig,
    categoryIdentifier,
    unitOfMeasure,
    priceCurrency,
    pageLocale,
    stockLevel,
    formatPriceOptions
  } = params

  const { singular, plural } = getMultibuyUnitLabel({
    categoryIdentifier,
    unitOfMeasure,
    multibuyUomCategories: shopConfig.multibuyUomCategories
  })

  return (multiBuyPrice ?? []).map((entry) => {
    const unitLabel = entry.qty === 1 ? singular : plural
    const attributeLabel = `${entry.qty} ${unitLabel}`.trim()
    const formattedPrice = formatPrice({
      value: entry.totalPrice,
      currency: priceCurrency,
      locale: pageLocale,
      options: formatPriceOptions
    })
    const formattedUnitPrice = formatPrice({
      value: entry.unitPrice,
      currency: priceCurrency,
      locale: pageLocale,
      options: formatPriceOptions
    })
    const pricePerUnitTemplate = getPricePerUnitLabel({
      categoryIdentifier,
      unitOfMeasure,
      multibuyUomCategories: shopConfig.multibuyUomCategories
    })
    const unitPriceLabel =
      entry.qty > 1 && pricePerUnitTemplate
        ? pricePerUnitTemplate.replace('$unit_price$', formattedUnitPrice ?? '')
        : undefined

    const badge =
      entry.bestValue && shopConfig.multibuyBestValueLabel
        ? shopConfig.multibuyBestValueLabel
        : undefined

    const disabled =
      stockLevel !== undefined && entry.qty > stockLevel ? true : undefined

    return {
      value: String(entry.qty),
      attributeLabel,
      price: formattedPrice,
      unitPrice: unitPriceLabel,
      badge,
      ...(disabled ? { disabled } : {})
    }
  })
}

export const getMultibuyAttributeSelectorProps = (
  params: MultibuyPropsParams
): AttributeSelectorProps => {
  const { shopConfig, selectedQty, onChange, isDisabled, stockLevel } = params

  const items = buildMultibuyItems(params)

  const attributeVariants = items.map((item) => ({
    ...item,
    ...(isDisabled || item.disabled ? { stock: StockStatuses.OutOfStock } : {})
  }))

  return {
    currentValue: String(selectedQty),
    ...(shopConfig.multibuyGroupTitle
      ? { label: shopConfig.multibuyGroupTitle }
      : {}),
    name: 'multibuy-qty',
    disableOutOfStock: true,
    attributeVariants,
    onAttributeChange: (e, attributeVariant) => {
      e?.preventDefault()
      const qty = Number(attributeVariant?.value)
      if (qty > 0 && (stockLevel === undefined || qty <= stockLevel)) {
        onChange(qty)
      }
    }
  }
}

export const getMultibuySelectProps = (
  params: MultibuyPropsParams
): MultibuySelectProps => {
  const { shopConfig, selectedQty, onChange, isDisabled, stockLevel } = params

  const items = buildMultibuyItems(params)

  return {
    items,
    value: String(selectedQty),
    ...(shopConfig.multibuyGroupTitle
      ? { 'aria-label': shopConfig.multibuyGroupTitle }
      : {}),
    ...(isDisabled ? { selectProps: { state: SelectStates.Disabled } } : {}),
    onChange: (value: string) => {
      const qty = Number(value)
      if (qty > 0 && (stockLevel === undefined || qty <= stockLevel)) {
        onChange(qty)
      }
    }
  }
}

export const getMultibuyHelperText = (params: {
  multiBuyPrice?: MultiBuyPriceEntry[]
  shopConfig: ShopConfig
  selectedQty: number
  priceCurrency?: string
  pageLocale?: string
  formatPriceOptions?: FormatPriceOptions
}): string | undefined => {
  const {
    multiBuyPrice,
    shopConfig,
    selectedQty,
    priceCurrency,
    pageLocale,
    formatPriceOptions
  } = params

  if (!shopConfig.multibuyPriceHelpText) {
    return undefined
  }

  const selectedEntry = multiBuyPrice?.find(
    (entry) => entry.qty === selectedQty
  )
  const singleEntry = multiBuyPrice?.find((entry) => entry.qty === 1)
  const difference =
    selectedEntry && singleEntry
      ? singleEntry.unitPrice * selectedEntry.qty - selectedEntry.totalPrice
      : 0
  const formattedDifference = formatPrice({
    value: difference,
    currency: priceCurrency,
    locale: pageLocale,
    options: formatPriceOptions
  })

  return shopConfig.multibuyPriceHelpText.replace(
    '$difference$',
    formattedDifference ?? ''
  )
}
